// Prepayment detail: the receipt header plus its application history, with
// an apply drawer that targets a later invoice. The apply itself is a
// financial mutation, so it is confirmed through the shared Dialog before
// `applyPrepayment` runs.
import { formatMoney, minorUnitsFor } from "@sylviq/ui-core";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Dialog } from "@sylviq/ui-react-web/components/ui/dialog";
import { Drawer } from "@sylviq/ui-react-web/components/ui/drawer";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import { PageHeader } from "@sylviq/ui-react-web/components/ui/page-header";
import {
  StatusDot,
  type StatusTone,
} from "@sylviq/ui-react-web/components/ui/status-dot";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@sylviq/ui-react-web/components/ui/table";
import { Link, useParams } from "@tanstack/react-router";
import { type FormEvent, useCallback, useEffect, useState } from "react";
import { InvoicePicker } from "@/components/Pickers";
import { CardField } from "@/components/RecordCard";
import {
  applyPrepayment,
  getPrepayment,
  type PrepaymentDetail,
} from "@/lib/api";
import { humanizeLabel, useInvoiceNumbers } from "@/lib/labels";
import { minorUnitEntry, toMinorUnits } from "@/lib/money";

function statusTone(status: string): StatusTone {
  switch (status.toLowerCase()) {
    case "applied":
      return "success";
    case "partially_applied":
      return "info";
    case "void":
      return "danger";
    default:
      return "idle";
  }
}

function plainStatus(status: string): string {
  return status
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function toErrorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

interface PendingApply {
  invoiceId: string;
  amountMinor: number;
}

export function PrepaymentDetailPage() {
  const { prepaymentId } = useParams({ strict: false }) as {
    prepaymentId: string;
  };
  const [detail, setDetail] = useState<PrepaymentDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{
    message: string;
    journalEntryId?: string;
  } | null>(null);
  const [drawer, setDrawer] = useState(false);
  const [pendingApply, setPendingApply] = useState<PendingApply | null>(null);
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);
  const invoiceNumbers = useInvoiceNumbers();
  const currency = detail?.currency_code || "USD";

  const reload = useCallback(() => {
    if (!prepaymentId) return;
    setError(null);
    getPrepayment(prepaymentId)
      .then(setDetail)
      .catch((cause: unknown) => setError(toErrorMessage(cause)));
  }, [prepaymentId]);

  useEffect(reload, [reload]);

  const canApply =
    detail !== null &&
    detail.unapplied_minor > 0 &&
    detail.status.toLowerCase() !== "void";

  const confirmApply = async () => {
    if (!pendingApply || !detail) return;
    setApplying(true);
    setApplyError(null);
    try {
      const result = await applyPrepayment(detail.id, {
        invoice_id: pendingApply.invoiceId,
        amount_minor: pendingApply.amountMinor,
      });
      setNotice({
        message: `Applied ${formatMoney({ cents: result.amount_minor, currency })} to the invoice.`,
        ...(result.journal_entry_id
          ? { journalEntryId: result.journal_entry_id }
          : {}),
      });
      setPendingApply(null);
      setDrawer(false);
      reload();
    } catch (cause) {
      setApplyError(toErrorMessage(cause));
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="grid gap-6">
      <nav aria-label="Breadcrumb" className="text-sm">
        <Link
          to="/prepayments"
          className="text-[var(--muted-foreground)] hover:text-[var(--foreground)] hover:underline"
        >
          Prepayments
        </Link>
        <span
          aria-hidden="true"
          className="mx-2 text-[var(--muted-foreground)]"
        >
          /
        </span>
        <span aria-current="page" className="font-medium">
          {detail?.customer_name ?? prepaymentId}
        </span>
      </nav>

      {error ? (
        <div
          role="alert"
          className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
        >
          {error}
          <div className="mt-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={reload}
            >
              Retry
            </Button>
          </div>
        </div>
      ) : null}

      {notice ? (
        <output className="surface block rounded-xl border border-[var(--success)] p-4 text-sm text-[var(--success)]">
          {notice.message}
          {notice.journalEntryId ? (
            <>
              {" "}
              <Link
                to="/journal/$entryId"
                params={{ entryId: notice.journalEntryId }}
                className="underline underline-offset-4"
              >
                View posted entry →
              </Link>
            </>
          ) : null}
        </output>
      ) : null}

      {detail === null && !error ? (
        <p className="text-sm text-[var(--muted-foreground)]">Loading…</p>
      ) : detail ? (
        <>
          <PageHeader
            title={detail.customer_name}
            description={
              <>
                Received {new Date(detail.received_at).toLocaleDateString()} via{" "}
                {humanizeLabel(detail.method)} ·{" "}
                <StatusDot
                  tone={statusTone(detail.status)}
                  label={plainStatus(detail.status)}
                />
              </>
            }
            actions={
              canApply ? (
                <Button type="button" size="sm" onClick={() => setDrawer(true)}>
                  Apply to invoice
                </Button>
              ) : null
            }
          />

          <div className="grid gap-4 sm:grid-cols-3">
            <div className="surface rounded-2xl p-5">
              <p className="text-xs font-medium text-[var(--muted-foreground)]">
                Received
              </p>
              <p className="mt-1 text-2xl font-semibold tabular-nums">
                {formatMoney({ cents: detail.amount_minor, currency })}
              </p>
            </div>
            <div className="surface rounded-2xl p-5">
              <p className="text-xs font-medium text-[var(--muted-foreground)]">
                Used so far
              </p>
              <p className="mt-1 text-2xl font-semibold tabular-nums">
                {formatMoney({
                  cents: detail.applied_amount_minor,
                  currency,
                })}
              </p>
            </div>
            <div className="surface rounded-2xl p-5">
              <p className="text-xs font-medium text-[var(--muted-foreground)]">
                Still available
              </p>
              <p className="mt-1 text-2xl font-semibold tabular-nums">
                {formatMoney({
                  cents: detail.unapplied_minor,
                  currency,
                })}
              </p>
            </div>
          </div>

          <section aria-label="Where it was used" className="grid gap-3">
            <h2 className="text-lg font-semibold tracking-tight">
              Where it was used
            </h2>
            <div className="surface hidden overflow-hidden rounded-2xl min-[64rem]:block">
              <div className="table-scroll">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Invoice</TableHead>
                      <TableHead>Applied on</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {detail.applications.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3}>
                          Not used against any invoice yet — apply it above.
                        </TableCell>
                      </TableRow>
                    ) : (
                      detail.applications.map((application) => (
                        <TableRow key={application.id}>
                          <TableCell>
                            <Link
                              to="/invoices/$invoiceId"
                              params={{ invoiceId: application.invoice_id }}
                              className="text-xs text-[var(--action)] underline-offset-4 hover:underline"
                              title={application.invoice_id}
                            >
                              {invoiceNumbers.get(application.invoice_id)
                                ? `View invoice ${invoiceNumbers.get(application.invoice_id)}`
                                : "View invoice"}{" "}
                              →
                            </Link>
                          </TableCell>
                          <TableCell className="whitespace-nowrap">
                            {new Date(
                              application.applied_at,
                            ).toLocaleDateString()}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {formatMoney({
                              cents: application.amount_minor,
                              currency,
                            })}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>

            <div className="stack-list grid gap-3 min-[64rem]:hidden">
              {detail.applications.length === 0 ? (
                <p className="text-sm text-[var(--muted-foreground)]">
                  Not used against any invoice yet — apply it above.
                </p>
              ) : (
                detail.applications.map((application) => (
                  <article
                    key={application.id}
                    className="stack-card rounded-xl border border-[var(--border)] bg-[var(--muted)] p-4"
                  >
                    <div className="stack-card-heading flex flex-wrap items-center justify-between gap-4 text-sm">
                      <Link
                        to="/invoices/$invoiceId"
                        params={{ invoiceId: application.invoice_id }}
                        className="record-title underline-offset-4 hover:underline"
                        title={application.invoice_id}
                      >
                        {invoiceNumbers.get(application.invoice_id)
                          ? `Invoice ${invoiceNumbers.get(application.invoice_id)}`
                          : "View invoice"}
                      </Link>
                      <span className="tabular-nums">
                        {formatMoney({
                          cents: application.amount_minor,
                          currency,
                        })}
                      </span>
                    </div>
                    <CardField label="Applied on">
                      {new Date(application.applied_at).toLocaleDateString()}
                    </CardField>
                  </article>
                ))
              )}
            </div>
          </section>
        </>
      ) : null}

      {drawer && detail ? (
        <ApplyPrepaymentDrawer
          customerName={detail.customer_name}
          availableMinor={detail.unapplied_minor}
          currency={currency}
          busy={applying}
          onClose={() => setDrawer(false)}
          onReview={(input) => {
            setApplyError(null);
            setPendingApply(input);
          }}
        />
      ) : null}

      <Dialog
        open={pendingApply !== null}
        onOpenChange={(open) => {
          if (!open && !applying) {
            setPendingApply(null);
            setApplyError(null);
          }
        }}
        title="Apply this prepayment?"
        description={
          pendingApply
            ? `Moves ${formatMoney({ cents: pendingApply.amountMinor, currency })} of ${detail?.customer_name ?? "this prepayment"} onto the selected invoice. The application posts immediately and moves no cash.`
            : undefined
        }
      >
        {applyError ? (
          <p role="alert" className="mb-3 text-sm text-[var(--danger)]">
            {applyError}
          </p>
        ) : null}
        <div className="actions-row">
          <Button
            type="button"
            variant="secondary"
            disabled={applying}
            onClick={() => {
              setPendingApply(null);
              setApplyError(null);
            }}
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={applying}
            onClick={() => void confirmApply()}
          >
            {applying ? "Applying…" : "Apply to invoice"}
          </Button>
        </div>
      </Dialog>
    </div>
  );
}

function ApplyPrepaymentDrawer({
  customerName,
  availableMinor,
  currency,
  busy,
  onClose,
  onReview,
}: {
  customerName: string;
  availableMinor: number;
  currency: string;
  busy: boolean;
  onClose: () => void;
  onReview: (input: PendingApply) => void;
}) {
  const code = currency || "USD";
  const entry = minorUnitEntry(code);
  const decimals = minorUnitsFor(code);
  const [invoiceId, setInvoiceId] = useState("");
  // Prefilled with the whole remaining balance; the user can lower it, but
  // never above what is still available (checked before confirmation).
  const [amount, setAmount] = useState(() =>
    availableMinor > 0
      ? (availableMinor / 10 ** decimals).toFixed(decimals)
      : "",
  );
  const [error, setError] = useState<string | null>(null);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    try {
      if (!invoiceId) throw new Error("Pick the invoice to apply this to.");
      const amount_minor = toMinorUnits(amount, code);
      if (amount_minor === null) throw new Error("Enter a positive amount.");
      if (amount_minor > availableMinor)
        throw new Error(
          `Only ${formatMoney({ cents: availableMinor, currency: code })} is still available from this prepayment.`,
        );
      onReview({ invoiceId, amountMinor: amount_minor });
    } catch (cause) {
      setError(toErrorMessage(cause));
    }
  };

  return (
    <Drawer
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title="Apply to an invoice"
      description="Moves part of this prepayment against a sent invoice. The amount must fit both what's left here and what the invoice still owes."
      className="w-[min(34rem,100%)]"
    >
      <form
        onSubmit={submit}
        className="grid gap-3"
        aria-label="Apply prepayment"
      >
        {error ? (
          <p role="alert" className="text-sm text-[var(--danger)]">
            {error}
          </p>
        ) : null}
        <label
          htmlFor="apply-invoice"
          className="grid gap-1 text-xs font-medium"
        >
          Invoice
          <InvoicePicker
            id="apply-invoice"
            value={invoiceId}
            onChange={setInvoiceId}
            disabled={busy}
            required
            customerName={customerName}
          />
        </label>
        <label
          htmlFor="apply-amount"
          className="grid gap-1 text-xs font-medium"
        >
          Amount ({code})
          <Input
            id="apply-amount"
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            required
            placeholder={entry.placeholder}
            step={entry.step}
            min="0"
            inputMode="decimal"
            disabled={busy}
          />
        </label>
        <Button type="submit" disabled={busy}>
          Review and apply
        </Button>
      </form>
    </Drawer>
  );
}
